// Question Bank — browse a published past-year question, click through to
// see its options and correct answer directly (no upload/grading step).
//
// Exam is no longer picked per-page — it comes from the student's own
// target_exam (set once in Settings). Search + chapter/topic/year filters
// replace the exam picker's old slot.
//
// question_latex is prose WITH embedded $...$ math (the LLM extraction's
// own convention), not a pure-math string — react-katex's BlockMath
// assumes the whole input is homogeneous math and either fails to parse
// it (falls back to raw red text) or, for a segment with no $ at all,
// renders plain English as math-mode symbols (which drops all whitespace).
// MathText is this codebase's own mixed prose+$...$ renderer (see
// RenderContent.jsx / QAView.jsx's established usage) — the correct tool
// for this exact shape.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MathText } from '../../components/RenderContent';
import {
  PenSquare, Check, AlertTriangle, ChevronLeft, Search, SlidersHorizontal, GraduationCap, Loader2,
} from 'lucide-react';
import api from './studentApi';
import { getExamPrepColors } from './examPrepTheme';
import { useStudent } from './StudentApp';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const EXAM_LABELS = { JEE_MAIN: 'JEE Main', JEE_ADVANCED: 'JEE Advanced', NEET: 'NEET', SAT: 'SAT', ITO: 'ITO' };
const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 20;

export default function PracticePyqPage() {
  const { me, theme } = useStudent();
  const examPrep = getExamPrepColors(theme?.id);
  const targetExam = me?.student_profile?.target_exam || '';

  const [difficulty, setDifficulty] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [taxonomyYear, setTaxonomyYear] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState('');
  const [topics, setTopics] = useState([]);
  const [topicId, setTopicId] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  const [questions, setQuestions] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const buildParams = (offset) => {
    const params = new URLSearchParams({ exam: targetExam, limit: String(PAGE_SIZE), offset: String(offset) });
    if (difficulty) params.set('difficulty', difficulty);
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (subjectId) params.set('subject_node_id', subjectId);
    if (chapterId) params.set('chapter_node_id', chapterId);
    if (topicId) params.set('concept_node_id', topicId);
    if (yearFilter) params.set('year', yearFilter);
    return params;
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = buildParams(questions?.length || 0);
      const d = await api.get(`/student/exam-prep/questions?${params.toString()}`);
      setQuestions(prev => [...(prev || []), ...(d.questions || [])]);
      setHasMore(!!d.has_more);
    } catch (e) {
      setError(e.message || 'Could not load more questions.');
    } finally {
      setLoadingMore(false);
    }
  };
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore; // always call the latest closure from the observer below

  // Infinite scroll — observe a sentinel element after the last card and
  // fetch the next page automatically once it's on screen, instead of a
  // "Load more" click. A callback ref (not useRef + a mount-once effect):
  // this component can render its `!targetExam`/`selected` early-return
  // branches on the very first paint (target_exam still loading from
  // /me), so the sentinel div doesn't exist yet at the moment a one-time
  // effect would run -- it would find a null ref and never retry, since
  // an empty-deps effect never fires again once the branch changes. A
  // callback ref instead fires every time the node actually (dis)mounts,
  // whichever render that happens on.
  const observerRef = useRef(null);
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) loadMoreRef.current();
    }, { rootMargin: '400px' });
    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Load the subject tree once, when the target exam is known.
  useEffect(() => {
    if (!targetExam) return;
    let alive = true;
    api.get(`/exam-taxonomy/${targetExam}/current`)
      .then(d => { if (alive) { setTaxonomyYear(d.year); setSubjects(d.subjects || []); } })
      .catch(() => { if (alive) { setTaxonomyYear(null); setSubjects([]); } });
    return () => { alive = false; };
  }, [targetExam]);

  useEffect(() => {
    setChapterId(''); setChapters([]); setTopicId(''); setTopics([]);
    if (!targetExam || !taxonomyYear || !subjectId) return;
    let alive = true;
    api.get(`/exam-taxonomy/${targetExam}/${taxonomyYear}/children/${subjectId}`)
      .then(d => alive && setChapters(d || [])).catch(() => alive && setChapters([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, taxonomyYear, targetExam]);

  useEffect(() => {
    setTopicId(''); setTopics([]);
    if (!targetExam || !taxonomyYear || !chapterId) return;
    let alive = true;
    api.get(`/exam-taxonomy/${targetExam}/${taxonomyYear}/children/${chapterId}`)
      .then(d => alive && setTopics(d || [])).catch(() => alive && setTopics([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, taxonomyYear, targetExam]);

  // Filters changed — reset to page 1 and replace the list (not append).
  useEffect(() => {
    if (!targetExam) return;
    let alive = true;
    setQuestions(null);
    setError(null);
    api.get(`/student/exam-prep/questions?${buildParams(0).toString()}`)
      .then(d => { if (!alive) return; setQuestions(d.questions || []); setHasMore(!!d.has_more); })
      .catch(e => alive && setError(e.message || 'Could not load practice questions.'));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetExam, difficulty, debouncedSearch, subjectId, chapterId, topicId, yearFilter]);

  if (selected) {
    return <QuestionWorkspace question={selected} onBack={() => setSelected(null)} />;
  }

  if (!targetExam) {
    return (
      <div className="space-y-5 max-w-3xl" data-testid="practice-pyq-page">
        <header className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: examPrep.goldTint, color: examPrep.gold }}>
            <PenSquare className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold opacity-60">Exam Prep</div>
            <h1 className="text-2xl font-extrabold mt-0.5" style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>Question Bank</h1>
          </div>
        </header>
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="pyq-no-target-exam">
          <GraduationCap className="w-8 h-8 mx-auto opacity-30 mb-2" />
          <p className="text-[13px] mb-3" style={{ color: 'var(--st-mute)' }}>Set which exam you&rsquo;re preparing for to browse the question bank.</p>
          <Link to="/student/settings" data-testid="pyq-set-target-exam-link"
            className="inline-block rounded-xl px-4 py-2 text-[12.5px] font-extrabold text-white" style={{ background: examPrep.gold }}>
            Set target exam in Settings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="practice-pyq-page">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: examPrep.goldTint, color: examPrep.gold }}>
          <PenSquare className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold opacity-60 flex items-center gap-1.5">
            Exam Prep <span style={{ color: examPrep.gold }}>&middot; {EXAM_LABELS[targetExam] || targetExam}</span>
          </div>
          <h1 className="text-2xl font-extrabold mt-0.5" style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>
            Question Bank
          </h1>
          <p className="text-[12.5px] mt-1" style={{ color: 'var(--st-mute)' }}>
            Browse real past-year questions by subject, chapter and difficulty, and reveal the answer and solution.
          </p>
        </div>
      </header>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 opacity-40" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search questions…"
          data-testid="pyq-search-input"
          className="w-full rounded-xl pl-10 pr-4 py-2.5 text-[13px] outline-none"
          style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {DIFFICULTIES.map(d => (
          <button key={d} onClick={() => setDifficulty(v => v === d ? '' : d)}
            data-testid={`pyq-difficulty-${d}`}
            className="px-3 py-1.5 rounded-full text-[11px] font-bold border capitalize transition-all"
            style={{
              borderColor: difficulty === d ? 'var(--st-primary)' : 'var(--st-border)',
              background: difficulty === d ? 'var(--st-primary)' : 'transparent',
              color: difficulty === d ? '#fff' : 'var(--st-ink)',
            }}>
            {d}
          </button>
        ))}
        <button onClick={() => setFiltersOpen(v => !v)} data-testid="pyq-filters-toggle"
          className="ml-auto px-3 py-1.5 rounded-full text-[11px] font-bold border inline-flex items-center gap-1.5"
          style={{ borderColor: filtersOpen ? examPrep.gold : 'var(--st-border)', color: filtersOpen ? examPrep.gold : 'var(--st-ink)' }}>
          <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
        </button>
      </div>

      {filtersOpen && (
        <div className="rounded-2xl border p-4 grid sm:grid-cols-4 gap-3" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="pyq-filter-panel">
          <FilterSelect label="Subject" value={subjectId} onChange={setSubjectId} testid="pyq-filter-subject"
            options={subjects.map(s => ({ value: s.id, label: s.name }))} placeholder="All subjects" />
          <FilterSelect label="Chapter" value={chapterId} onChange={setChapterId} testid="pyq-filter-chapter"
            options={chapters.map(c => ({ value: c.id, label: c.name }))} placeholder="All chapters" disabled={!subjectId} />
          <FilterSelect label="Topic" value={topicId} onChange={setTopicId} testid="pyq-filter-topic"
            options={topics.map(t => ({ value: t.id, label: t.name }))} placeholder="All topics" disabled={!chapterId} />
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-60 block mb-1">Year</label>
            <input type="number" value={yearFilter} onChange={e => setYearFilter(e.target.value)}
              placeholder="Any year" data-testid="pyq-filter-year"
              className="w-full rounded-lg px-2.5 py-2 text-[12.5px] outline-none"
              style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }} />
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-2xl border p-4 text-[13px] flex items-center gap-2" style={{ borderColor: '#FCA5A5', color: '#B91C1C', background: '#FEF2F2' }}>
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {questions === null && !error && (
        <div className="text-[13px] opacity-60 py-8 text-center">Loading questions…</div>
      )}

      {questions !== null && questions.length === 0 && !error && (
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="pyq-empty">
          <PenSquare className="w-8 h-8 mx-auto opacity-30 mb-2" />
          <p className="text-[13px]" style={{ color: 'var(--st-mute)' }}>No published questions match your filters yet.</p>
        </div>
      )}

      <div className="space-y-2.5">
        {(questions || []).map(q => (
          <button
            key={q.id}
            onClick={() => setSelected(q)}
            data-testid={`pyq-item-${q.id}`}
            className="w-full text-left rounded-2xl border p-4 transition-all hover:shadow-md"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', borderLeft: `3px solid ${examPrep.gold}` }}>
            <div className="flex items-center gap-2 mb-2 text-[10px] font-bold uppercase tracking-wider opacity-60">
              <span>{q.year}</span><span>&middot;</span><span className="capitalize">{q.difficulty}</span>
              <span>&middot;</span><span className="capitalize">{(q.question_type || '').replace('_', '-')}</span>
            </div>
            <div className="overflow-x-auto text-[13px]" style={{ color: 'var(--st-ink)' }}>
              <MathText text={q.question_latex} />
            </div>
          </button>
        ))}
      </div>

      {/* Infinite scroll sentinel — the callback ref above attaches the
          observer whenever this div actually mounts, on whatever render
          that happens to be. */}
      <div ref={sentinelRef} className="flex justify-center py-4" data-testid="pyq-infinite-scroll-sentinel">
        {loadingMore && (
          <span className="text-[12px] font-semibold inline-flex items-center gap-2" style={{ color: 'var(--st-mute)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </span>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, placeholder, testid, disabled }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider opacity-60 block mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid}
        className="w-full rounded-lg px-2.5 py-2 text-[12.5px] outline-none disabled:opacity-50"
        style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ─── Question detail: options + correct answer + solution ─────────────────
function QuestionWorkspace({ question, onBack }) {
  const { theme } = useStudent();
  const examPrep = getExamPrepColors(theme?.id);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setAnswer(null);
    setError(null);
    api.get(`/student/exam-prep/questions/${question.id}/answer`)
      .then(d => alive && setAnswer(d))
      .catch(e => alive && setError(e.message || 'Could not load the answer.'));
    return () => { alive = false; };
  }, [question.id]);

  const options = question.options_latex || [];
  const correctIndex = answer ? 'ABCD'.indexOf(String(answer.correct_answer || '').trim().toUpperCase()) : -1;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="pyq-workspace">
      <button onClick={onBack} data-testid="pyq-back" className="text-[12px] font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ChevronLeft className="w-4 h-4" /> Back to questions
      </button>

      <div className="rounded-2xl border p-5 space-y-4" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', borderLeft: `3px solid ${examPrep.gold}` }}>
        <div>
          <div className="flex items-center gap-2 mb-2 text-[10px] font-bold uppercase tracking-wider opacity-60">
            <span>{question.exam.replace('_', ' ')}</span><span>&middot;</span><span>{question.year}</span>
            <span>&middot;</span><span className="capitalize">{question.difficulty}</span>
          </div>
          <div className="overflow-x-auto text-[14px]" style={{ color: 'var(--st-ink)' }}>
            <MathText text={question.question_latex} />
          </div>
        </div>

        {options.length > 0 && (
          <div className="space-y-2">
            {options.map((opt, i) => {
              const isCorrect = answer && i === correctIndex;
              return (
                <div key={i} data-testid={`pyq-option-${i}`}
                  className="flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 border-2"
                  style={{ borderColor: isCorrect ? '#16A34A' : 'var(--st-border)', background: isCorrect ? 'color-mix(in srgb, #16A34A 12%, transparent)' : 'transparent' }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                    style={{ background: isCorrect ? '#16A34A' : 'var(--st-bg)', color: isCorrect ? '#fff' : 'var(--st-ink)', border: isCorrect ? 'none' : '1px solid var(--st-border)' }}>
                    {isCorrect ? <Check className="w-3.5 h-3.5" /> : String.fromCharCode(65 + i)}
                  </span>
                  <div className="text-[13px] flex-1 min-w-0 overflow-x-auto" style={{ color: 'var(--st-ink)' }}><MathText text={opt} /></div>
                </div>
              );
            })}
          </div>
        )}

        {options.length === 0 && answer && (
          <div className="rounded-xl px-3.5 py-2.5 border-2 inline-flex items-center gap-2" style={{ borderColor: '#16A34A', background: 'color-mix(in srgb, #16A34A 12%, transparent)' }} data-testid="pyq-numerical-answer">
            <Check className="w-4 h-4" style={{ color: '#16A34A' }} />
            <span className="text-[13px] font-bold" style={{ color: 'var(--st-ink)' }}>Answer: <MathText text={String(answer.correct_answer)} /></span>
          </div>
        )}

        {!answer && !error && (
          <div className="text-[12.5px] opacity-60">Loading answer…</div>
        )}

        {error && (
          <div className="rounded-xl border p-3 text-[12px] flex items-center gap-2" style={{ borderColor: '#FCA5A5', color: '#B91C1C', background: '#FEF2F2' }}>
            <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {answer?.solution_latex && (
          <div className="pt-3 border-t" style={{ borderColor: 'var(--st-border)' }}>
            <div className="text-[11px] font-extrabold uppercase tracking-wider mb-2" style={{ color: examPrep.gold }}>Solution</div>
            <div className="text-[13px] leading-relaxed overflow-x-auto" style={{ color: 'var(--st-ink)' }}>
              <MathText text={answer.solution_latex} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
